/**
 * GgfBadge — Guardian Garage Floors certification chip.
 *
 * Renders a single, consistent badge across the platform whenever a
 * talent is GGF-certified. Use either:
 *
 *   <GgfBadge certified score={95} date="2026-04-30" />
 *   <GgfBadge talent={talentObj} />          // picks ggf_* fields automatically
 *
 * Size variants: 'xs' | 'sm' | 'md' (default). 'compact' (boolean) renders
 * the bare shield icon with a tooltip (for tight card grids).
 *
 * Iter-246 — Sprint 1: GGF visibility surface across Discovery,
 * Applicant Review, Staffing, Quick Book, Profile, Recommendations.
 *
 * Iter-252 / Iter-254 — GGF VISIBILITY RULES (user request 2026-06-16):
 *   Only render the GGF badge when ONE of the following is true:
 *     • the current logged-in user is admin / super_admin
 *     • the current talent is viewing their OWN profile
 *     • the badge is being rendered inside a Guardian Garage Floors client
 *       surface (client company matches GGF OR a parent `requiresGgf` shift
 *       context is active)
 *   Pass `context` to opt in: { isAdmin, isOwnProfile, isGgfClient, requiresGgf }
 *
 *   Iter-254 — Default is now CLOSED. If a caller forgets to pass `context`
 *   the badge does NOT render. This prevents accidental leakage of the
 *   client-specific certification onto generic discovery / booking
 *   surfaces. Existing callers that legitimately want the badge (admin
 *   panels, GGF client surfaces, talent's own profile) MUST pass context.
 */
import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { formatDate } from '../utils/timeFormat';

const sizeClasses = {
  xs: 'text-[10px] px-1.5 py-0.5 gap-1',
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1.5',
};

const iconSizes = { xs: 'w-3 h-3', sm: 'w-3.5 h-3.5', md: 'w-4 h-4' };

const fmtScore = (s) => {
  if (s == null || s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) ? `${n}%` : `${n.toFixed(1)}%`;
};

// Iter-252 — GGF client identifier guard. Add additional client IDs
// here as Guardian Garage Floors expands sub-accounts.
const GGF_CLIENT_NAMES = ['guardian garage floors', 'ggf'];

const GgfBadge = ({
  certified,
  score,
  date,
  source,
  talent,
  size = 'sm',
  compact = false,
  showScore = true,
  showDate = false,
  className = '',
  // Iter-252 — visibility context
  context,
}) => {
  // Accept either explicit props or a talent object with snake_case fields.
  const isCertified = certified ?? talent?.ggf_certified;
  if (!isCertified) return null;

  // Iter-252 / Iter-254 — Visibility is now CLOSED-BY-DEFAULT. Callers must
  // pass an explicit `context` object granting permission. This prevents
  // GGF leakage onto generic client surfaces (Discovery, Quick Book,
  // Applicants) where the certification is irrelevant.
  const ctx = context || {};
  const isAdmin     = !!ctx.isAdmin;
  const isOwn       = !!ctx.isOwnProfile;
  const isGgfClient = !!ctx.isGgfClient;
  const requiresGgf = !!ctx.requiresGgf;
  if (!(isAdmin || isOwn || isGgfClient || requiresGgf)) return null;

  const rawScore = score ?? talent?.ggf_test_score;
  const rawDate = date ?? talent?.ggf_certified_at;
  const rawSource = source ?? talent?.ggf_certified_source;

  const scoreText = fmtScore(rawScore);
  const sz = sizeClasses[size] || sizeClasses.sm;
  const iz = iconSizes[size] || iconSizes.sm;

  const tooltipParts = ['Guardian Garage Floors Certified'];
  if (scoreText) tooltipParts.push(`Score: ${scoreText}`);
  if (rawDate) tooltipParts.push(`Completed: ${formatDate(rawDate)}`);
  if (rawSource === 'admin_manual' || rawSource === 'admin_manual_training') {
    tooltipParts.push('Verified by admin');
  }
  const tooltip = tooltipParts.join(' · ');

  if (compact) {
    return (
      <span
        title={tooltip}
        className={`inline-flex items-center justify-center rounded-full bg-emerald-100 border border-emerald-300 text-emerald-700 w-5 h-5 ${className}`}
        data-testid="ggf-badge-compact"
      >
        <ShieldCheck className="w-3 h-3" />
      </span>
    );
  }

  return (
    <span
      title={tooltip}
      className={`inline-flex items-center rounded-full font-semibold bg-emerald-50 border border-emerald-300 text-emerald-800 ${sz} ${className}`}
      data-testid="ggf-badge"
    >
      <ShieldCheck className={`${iz} text-emerald-600 flex-shrink-0`} />
      <span>GGF Certified</span>
      {showScore && scoreText && (
        <span className="text-emerald-700 font-medium" data-testid="ggf-badge-score">· {scoreText}</span>
      )}
      {showDate && rawDate && (
        <span className="text-emerald-600 font-normal hidden sm:inline">· {formatDate(rawDate)}</span>
      )}
    </span>
  );
};

// Iter-252 — Helper: derive GGF visibility context from a client object.
// Use in client-facing recommendation widgets so the badge stays hidden
// unless the viewer is actually Guardian Garage Floors.
export const isGgfClientCompany = (companyName) => {
  if (!companyName) return false;
  const n = String(companyName).toLowerCase().trim();
  return GGF_CLIENT_NAMES.some((s) => n.includes(s));
};

export default GgfBadge;

